import React, { useEffect } from 'react';
import { Shield, FileText, Scale, Users } from 'lucide-react';

const TermsPage = () => {
  // Scroll to top when the page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = "April 2, 2026";

  return (
    <div className="min-h-screen bg-stone-50 py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden">
        
        {/* Header Section */}
        <div className="bg-[#1A4D2E] px-8 py-10 text-center">
          <FileText className="mx-auto h-12 w-12 text-white mb-4 opacity-90" />
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Terms of Service
          </h1>
          <p className="text-[#E8F3EB] text-sm md:text-base">
            Last Updated: {lastUpdated}
          </p>
        </div>

        {/* Content Section */}
        <div className="px-8 py-10 text-stone-700 space-y-8 leading-relaxed">
          
          <section>
            <h2 className="text-2xl font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <Shield className="text-[#1A4D2E]" size={24} />
              1. Acceptance of Terms
            </h2>
            <p>
              By accessing or using the Albriks Hub platform, website, and mobile applications (collectively, the "Service"), you agree to be bound by these Terms of Service. If you are using the Service on behalf of a real estate brokerage, company, or organization, you represent that you have the authority to bind that entity to these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <Users className="text-[#1A4D2E]" size={24} />
              2. User Accounts and Responsibilities
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Account Security:</strong> You are responsible for safeguarding the password and credentials used to access your Albriks Hub Agent, Broker, or Service Provider account.</li>
              <li><strong>Accurate Information:</strong> You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate.</li>
              <li><strong>Lead Data Management:</strong> As a real estate professional using our open house sign-in and CRM tools, you are solely responsible for ensuring that your collection, use, and storage of visitor and lead data complies with all applicable local, state, and federal privacy laws (including but not limited to the TCPA).</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-stone-900 mb-4">
              3. Platform Usage & Restrictions
            </h2>
            <p className="mb-3">You agree not to engage in any of the following prohibited activities:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Using the Service for any illegal purpose or in violation of any local, state, national, or international law, including real estate commission regulations.</li>
              <li>Attempting to bypass, disable, or interfere with security-related features of the Service.</li>
              <li>Uploading or transmitting viruses, malware, or destructive code, including files exceeding our specified size limits (e.g., 50MB).</li>
              <li>Scraping, mining, or extracting data from the Service without express written permission.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <Scale className="text-[#1A4D2E]" size={24} />
              4. Disclaimer of Warranties
            </h2>
            <p>
              The Service is provided on an "AS IS" and "AS AVAILABLE" basis. Albriks Hub makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property.
            </p>
            <p className="mt-3">
              Furthermore, Albriks Hub does not guarantee the conversion of leads, the performance of service providers in our referral pool, or the continuous availability of the platform without interruption.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-stone-900 mb-4">
              5. Limitation of Liability
            </h2>
            <p>
              In no event shall Albriks Hub, its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your access to or use of or inability to access or use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-stone-900 mb-4">
              6. Changes to Terms
            </h2>
            <p>
              We reserve the right, at our sole discretion, to modify or replace these Terms at any time. By continuing to access or use our Service after those revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, please stop using the Service.
            </p>
          </section>

          <section className="bg-stone-100 p-6 rounded-xl mt-8">
            <h3 className="text-lg font-semibold text-stone-900 mb-2">Contact Us</h3>
            <p className="text-sm">
              If you have any questions about these Terms, please contact us at: <br/>
              <a href="mailto:support@albriks.com" className="text-[#1A4D2E] font-medium hover:underline">support@albriks.com</a>
            </p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default TermsPage;
